// Surface 03 — la veille, en consultation.
//
// CE QUE CETTE PAGE N'EST PLUS. Elle s'appelait « Veille & décisions » et
// portait les deux : le tri du matin et le suivi des propales. C'est de là que
// venaient ses six listes déroulantes — il fallait bien un moyen de retrouver
// une chose au milieu de l'autre. Le tri est parti dans `Decisions`, le suivi
// dans `Suivi`, et il ne reste ici que ce que les filtres servent vraiment :
// retrouver une offre précise dans tout ce qui a été capté.
//
// LES FILTRES RESTENT DONC, ET C'EST VOULU. « Toutes les offres Java chez X en
// juin » est un besoin réel ; il n'a simplement rien à faire sur l'écran où
// l'on tranche 150 lignes à la chaîne. Ils ne coûtent plus rien à personne ici,
// puisque c'est la seule chose que cette page fait.
//
// AUCUNE ACTION DE TRI depuis cette page : pas de sélection multiple, pas de
// bouton « écarter ». Écarter est une décision, et les décisions se prennent
// là où on les voit toutes. Une offre s'ouvre, et c'est tout.

import { useMemo, useState } from 'react'
import {
  ArrowDown, ArrowUp, ChevronsUpDown, ExternalLink, Plus, RotateCcw, Search, X,
} from 'lucide-react'
import { get, post, type OfferRow } from '../lib/api'
import { relTime, initials, teinte, offerStatus } from '../lib/format'
import { navigate, useQuery } from '../lib/router'
import { useData, Loading, Empty, Score, Modal, ActionButton, useToast } from '../components/common'
import { useFormule, useProfilUnique } from '../lib/formule'
import { useMots } from '../lib/vocabulaire'
import { useCandidatureStatus } from '../lib/dictionnaires'
import { useLang } from '../lib/lang'
import { useT } from '../lib/i18n'
import { Employeur } from '../components/sites'

type Periode = 'all' | 'today' | '7d' | '30d'
type Tri = 'score' | 'date' | 'entreprise' | 'titre' | 'statut'

function usePeriodes(): { id: Periode; label: string; days: number | null }[] {
  const t = useT()
  return [
    { id: 'all', label: t('veille.periodAll'), days: null },
    { id: 'today', label: t('veille.periodToday'), days: 1 },
    { id: '7d', label: t('veille.period7d'), days: 7 },
    { id: '30d', label: t('veille.period30d'), days: 30 },
  ]
}

/** Sens par défaut de chaque tri : celui qui met en tête ce qu'on cherche. */
const SENS_PAR_DEFAUT: Record<Tri, 'asc' | 'desc'> = {
  score: 'desc',
  date: 'desc',
  entreprise: 'asc',
  titre: 'asc',
  statut: 'asc',
}

const ORDRE_STATUT: Record<string, number> = { todo: 0, later: 1, skip: 2 }

const meilleurScore = (o: OfferRow) =>
  o.matches.length ? Math.max(...o.matches.map((m) => m.score)) : -1

export function Veille({ onChanged }: { onChanged: () => void }) {
  const profilUnique = useProfilUnique()
  const mots = useMots()
  const t = useT()
  const lang = useLang()
  const PERIODES = usePeriodes()
  const OFFER_STATUS = offerStatus(useFormule(), lang)
  const offers = useData(() => get<OfferRow[]>('/api/offers'), [])
  const [params, setParams] = useQuery()
  const [manualOpen, setManualOpen] = useState(false)

  // Tout l'état de l'écran vient de l'URL : un seul endroit où le lire, un seul
  // à réécrire — et il survit au retour depuis une offre comme au rechargement.
  const q = params.get('q') ?? ''
  const entreprise = params.get('entreprise') ?? 'all'
  const lieu = params.get('lieu') ?? 'all'
  const periode = (params.get('periode') as Periode) ?? 'all'
  const consultant = params.get('consultant') ?? 'all'
  const statut = params.get('statut') ?? 'all'
  const tri = (params.get('tri') as Tri) ?? 'date'
  const sens = (params.get('sens') as 'asc' | 'desc') ?? SENS_PAR_DEFAUT[tri] ?? 'desc'

  const rows = offers.data ?? []

  // Les listes déroulantes viennent des offres RÉELLEMENT présentes : proposer
  // un filtre qui ne renverrait rien n'aide personne.
  const listes = useMemo(() => {
    const uniq = (xs: (string | null)[]) =>
      [...new Set(xs.filter((x): x is string => !!x && x.trim() !== ''))].sort((a, b) => a.localeCompare(b, 'fr'))
    return {
      entreprises: uniq(rows.map((o) => o.company).filter((c): c is string => !!c)),
      lieux: uniq(rows.map((o) => o.location)),
      consultants: uniq(rows.flatMap((o) => o.matches.map((m) => m.name))),
    }
  }, [rows])

  const filtrees = useMemo(() => {
    const needle = q.trim().toLowerCase()
    const jours = PERIODES.find((p) => p.id === periode)?.days ?? null
    const limite = jours ? Date.now() - jours * 86400_000 : null
    const retenues = rows.filter((o) => {
      if (entreprise !== 'all' && o.company !== entreprise) return false
      if (lieu !== 'all' && o.location !== lieu) return false
      if (consultant !== 'all' && !o.matches.some((m) => m.name === consultant)) return false
      if (statut !== 'all' && o.status !== statut) return false
      if (limite && new Date(o.first_seen_at.replace(' ', 'T') + 'Z').getTime() < limite) return false
      if (needle && !`${o.company ?? ''} ${o.source_name} ${o.title} ${o.location ?? ''}`.toLowerCase().includes(needle)) return false
      return true
    })

    const signe = sens === 'asc' ? 1 : -1
    const comparer: Record<Tri, (a: OfferRow, b: OfferRow) => number> = {
      score: (a, b) => meilleurScore(a) - meilleurScore(b),
      date: (a, b) => a.first_seen_at.localeCompare(b.first_seen_at),
      entreprise: (a, b) => (a.company ?? '\uffff').localeCompare(b.company ?? '\uffff', 'fr'),
      titre: (a, b) => a.title.localeCompare(b.title, 'fr'),
      statut: (a, b) => (ORDRE_STATUT[a.status] ?? 9) - (ORDRE_STATUT[b.status] ?? 9),
    }
    // Départage par fraîcheur : à score égal, la plus récente d'abord. Sans ce
    // second critère, l'ordre de deux offres à 82 dépendrait de la requête SQL.
    return [...retenues].sort(
      (a, b) => signe * comparer[tri](a, b) || -a.first_seen_at.localeCompare(b.first_seen_at)
    )
  }, [rows, entreprise, lieu, consultant, statut, periode, q, tri, sens])

  const filtresActifs =
    [entreprise, lieu, consultant, statut].filter((v) => v !== 'all').length +
    (periode !== 'all' ? 1 : 0) +
    (q ? 1 : 0)

  const reinitialiser = () =>
    setParams({ entreprise: null, lieu: null, consultant: null, statut: null, periode: null, q: null })

  function trierPar(colonne: Tri) {
    // Recliquer la colonne active inverse le sens ; changer de colonne repart
    // du sens qui a du sens pour elle (score décroissant, entreprise A→Z).
    if (tri === colonne) setParams({ sens: sens === 'asc' ? 'desc' : 'asc' })
    else setParams({ tri: colonne, sens: SENS_PAR_DEFAUT[colonne] })
  }

  if (offers.loading) return <Loading label={t('veille.loading')} />
  if (offers.error) return <Empty>{offers.error}</Empty>

  const entete = (colonne: Tri, libelle: string) => (
    <th aria-sort={tri === colonne ? (sens === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button className={`sortbtn${tri === colonne ? ' on' : ''}`} onClick={() => trierPar(colonne)}>
        {libelle}
        {tri === colonne
          ? sens === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
          : <ChevronsUpDown size={12} className="sorthint" />}
      </button>
    </th>
  )

  return (
    <>
      <div className="bar">
        <div>
          <h3>{mots('nav.veille')}</h3>
          <div className="sub">{t(rows.length > 1 ? 'veille.subtitle' : 'veille.subtitleSingular', { n: rows.length })}</div>
        </div>
        <div className="grow" />
        <div className="search">
          <Search size={14} />
          <input
            value={q}
            onChange={(e) => setParams({ q: e.target.value })}
            placeholder={t('veille.searchPlaceholder')}
            aria-label={t('veille.searchAria')}
          />
          {q && (
            <button className="searchclear" onClick={() => setParams({ q: null })} aria-label={t('veille.clearSearch')}>
              <X size={13} />
            </button>
          )}
        </div>
        <button className="btn-secondary" onClick={() => setManualOpen(true)}>
          <Plus size={15} />{t('veille.manualEntryButton')}
        </button>
      </div>

      <div className="body">
        <div className="filters">
          <Select value={entreprise} onChange={(v) => setParams({ entreprise: v })} all={t('veille.allCompanies')} options={listes.entreprises} />
          <Select value={lieu} onChange={(v) => setParams({ lieu: v })} all={t('veille.allLocations')} options={listes.lieux} />
          {/* Filtrer PAR consultant n'a de sens qu'avec plusieurs profils. En
              Radar on est le seul, donc la liste n'aurait qu'une entrée et le
              filtre ne réduirait jamais rien. */}
          {!profilUnique && (
            <Select value={consultant} onChange={(v) => setParams({ consultant: v })} all={t('veille.allConsultants')} options={listes.consultants} />
          )}
          <select
            className={`chip${periode !== 'all' ? ' on' : ''}`}
            value={periode}
            onChange={(e) => setParams({ periode: e.target.value })}
            aria-label={t('veille.periodAria')}
          >
            {PERIODES.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <select
            className={`chip${statut !== 'all' ? ' on' : ''}`}
            value={statut}
            onChange={(e) => setParams({ statut: e.target.value })}
            aria-label={t('veille.statusAria')}
          >
            <option value="all">{t('veille.allStatuses')}</option>
            {Object.entries(OFFER_STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>

          <div className="grow" />
          {/* Le décompte est À CÔTÉ des filtres, pas dans le sous-titre : c'est
              lui qui dit si le filtre qu'on vient de poser a fait quelque chose. */}
          <span className="filtercount">
            {t(filtrees.length > 1 ? 'veille.shownPlural' : 'veille.shownSingular', { n: filtrees.length })}
          </span>
          {filtresActifs > 0 && (
            <button className="chip" onClick={reinitialiser}>
              <RotateCcw size={12} />{t('veille.reset', { n: filtresActifs })}
            </button>
          )}
        </div>

        <div className="tblwrap">
          <table className="tbl">
            <thead>
              <tr>
                <th style={{ width: 22 }} />
                {entete('titre', t('veille.colTitle'))}
                {entete('entreprise', t('veille.colCompany'))}
                {entete('date', t('veille.colDetected'))}
                {entete('score', mots('veille.colonneProfil'))}
                {entete('statut', t('veille.colStatus'))}
              </tr>
            </thead>
            <tbody>
              {filtrees.map((o) => (
                <tr
                  key={o.id}
                  className={'row' + (o.read ? '' : ' unread') + (o.status === 'skip' ? ' skipped' : '')}
                  tabIndex={0}
                  role="link"
                  onClick={() => navigate(`#/offre/${o.id}`)}
                  onKeyDown={(e) => {
                    // Espace autant qu'Entrée : une ligne qui se comporte comme
                    // un lien doit répondre aux deux, sinon elle n'est ouvrable
                    // qu'à la souris pour la moitié des gens.
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      navigate(`#/offre/${o.id}`)
                    }
                  }}
                >
                  <td>{o.read ? <span className="rdot" /> : <span className="udot" title={t('veille.unread')} />}</td>
                  <td>
                    <div className="ti strong">
                      {o.title}
                      {o.kind === 'mission' && <span className="kindbadge kb-mission">{t('veille.missionBadge')}</span>}
                    </div>
                  </td>
                  <td>
                    <div className="co">
                      <Employeur nom={o.company} source={o.source_name} />
                      {o.url && (
                        <a
                          className="ext"
                          href={o.url}
                          target="_blank"
                          rel="noopener"
                          title={t('veille.viewOriginal')}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                    {/* Le lieu n'a plus sa colonne : il était vide sur la
                        plupart des offres et poussait deux colonnes utiles
                        hors de l'écran. */}
                    {/* En Radar, la provenance (le site) et non l'adaptateur
                        (`generic`, `jsonapi`), détail d'exploitant. Elle est
                        déjà dite à la place de l'employeur quand il manque. */}
                    <div className="src-sub">
                      {profilUnique
                        ? [o.company ? o.source_name : null, o.location].filter(Boolean).join(' · ')
                        : <>
                            {o.platform === 'manual' ? t('veille.manualPlatform') : o.platform}
                            {o.location && ` · ${o.location}`}
                          </>}
                    </div>
                  </td>
                  <td><span className="when">{relTime(o.first_seen_at)}</span></td>
                  <td><MeilleurProfil matches={o.matches} /></td>
                  <td><EtatOffre offre={o} /></td>
                </tr>
              ))}
              {filtrees.length === 0 && (
                <tr>
                  <td colSpan={6}>
                    <Empty>
                      {t('veille.noMatch')}
                      {filtresActifs > 0 && (
                        <> <button className="linkbtn" onClick={reinitialiser}>{t('veille.resetFilters')}</button></>
                      )}
                    </Empty>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {manualOpen && <SaisieManuelle onClose={() => setManualOpen(false)} onDone={onChanged} />}
    </>
  )
}

/**
 * L'état d'une offre en une cellule : son statut de tri, et ce qui se joue
 * dessus s'il se joue quelque chose.
 *
 * Les deux niveaux sont montrés ENSEMBLE parce qu'ils ne disent pas la même
 * chose et qu'aucun ne remplace l'autre : « à décider » plus « Marc en
 * entretien » est un état parfaitement cohérent, c'est même le plus courant
 * dès qu'un second profil peut être proposé sur la même offre.
 */
function EtatOffre({ offre }: { offre: OfferRow }) {
  const ETAT = useCandidatureStatus()
  const t = useT()
  const profilUnique = useProfilUnique()
  const OFFER_STATUS = offerStatus(useFormule(), useLang())
  // La plus avancée, refus mis à part : un refus ne décrit l'offre que s'il
  // est le seul mouvement (même règle que côté serveur, cf. queries.ts).
  const ordre = Object.keys(ETAT)
  const rang = (s: string) => (s === 'refuse' ? -1 : ordre.indexOf(s))
  const phare = offre.candidatures.length
    ? offre.candidatures.reduce((a, b) => (rang(b.status) > rang(a.status) ? b : a))
    : null

  // En Radar, « Nouvelle » sur une offre que Radar n'a jamais évaluée pour
  // moi la ferait passer pour une proposition (issue #77) ; et une offre où
  // j'ai postulé n'est plus à trier, son étape suffit.
  const nonEvaluee = profilUnique && offre.matches.length === 0
  const statut = profilUnique && phare
    ? null
    : nonEvaluee && offre.status === 'todo'
      ? <span className="status st-none"><span className="sd" />{t('veille.notScored')}</span>
      : <span className={`status st-${offre.status}`}><span className="sd" />{OFFER_STATUS[offre.status]}</span>

  return (
    <div className="etat">
      {statut}
      {phare && (
        <span className={`kchip sm ${ETAT[phare.status].cls}`}>
          <span className="kd" />
          {/* Son propre prénom devant son étape, sur chaque ligne : du
              langage de tiers. */}
          {!profilUnique && <>{phare.name.split(' ')[0]} · </>}{ETAT[phare.status].label}
          {offre.candidatures.length > 1 && <span className="pc-more">+{offre.candidatures.length - 1}</span>}
        </span>
      )}
    </div>
  )
}

/**
 * Le meilleur profil, et le nombre des autres.
 *
 * Les trois profils étaient empilés verticalement : chaque ligne faisait trois
 * lignes de haut, on voyait sept offres par écran au lieu de dix-huit, et la
 * colonne était assez large pour repousser le statut hors du champ. Or la
 * décision se prend sur le MEILLEUR — les suivants se lisent dans la fiche.
 */
function MeilleurProfil({ matches }: { matches: OfferRow['matches'] }) {
  const profilUnique = useProfilUnique()
  if (matches.length === 0) return <span className="when">—</span>
  // En Radar, la colonne s'appelle « Mon score » : mon propre nom et mes
  // initiales devant, sur chaque ligne, sont du langage de tiers.
  if (profilUnique) return <Score value={matches[0].score} />
  const tries = [...matches].sort((a, b) => b.score - a.score)
  const [premier, ...autres] = tries
  return (
    <div className="bestmatch">
      <span className="matchchip" title={premier.label}>
        <span className="miniav" style={teinte(premier.name)}>{initials(premier.name)}</span>
        {premier.name.split(' ')[0]}
        <Score value={premier.score} />
      </span>
      {autres.length > 0 && (
        <span
          className="morematch"
          title={autres.map((m) => `${m.name} : ${m.score}`).join('\n')}
        >
          +{autres.length}
        </span>
      )}
    </div>
  )
}

function Select({
  value,
  onChange,
  all,
  options,
}: {
  value: string
  onChange: (v: string) => void
  all: string
  options: string[]
}) {
  if (options.length === 0) return null
  return (
    <select
      className={`chip${value !== 'all' ? ' on' : ''}`}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      aria-label={all}
    >
      <option value="all">{all}</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  )
}

/**
 * Porte d'entrée B du cadrage : un cahier des charges client collé à la main.
 *
 * Exportée parce que la pile de décisions l'ouvre aussi : c'est le geste par
 * lequel une offre entre dans la console sans passer par une source, et il doit
 * être à portée des deux écrans où l'on regarde ce qui est à traiter.
 */
export function SaisieManuelle({ onClose, onDone, url }: { onClose: () => void; onDone: () => void; url?: string }) {
  const mots = useMots()
  const t = useT()
  const toast = useToast()
  // `url` : venue d'« Ajouter une offre par son lien » quand la page n'a pas
  // pu être lue. Elle est gardée avec l'offre, pour « Annonce d'origine ».
  const [form, setForm] = useState({ title: '', company: '', location: '', description: '', url: url ?? '' })
  const set = (k: keyof typeof form) => (e: any) => setForm({ ...form, [k]: e.target.value })

  return (
    <Modal
      title={mots('veille.manuelle')}
      onClose={onClose}
      footer={
        <>
          <button className="btn-secondary" onClick={onClose}>{t('common.cancel')}</button>
          <ActionButton
            variant="primary"
            busyLabel={t('veille.evaluating')}
            onClick={async () => {
              // Pas de try/catch : ActionButton remonte l'échec en notification.
              // L'attraper ici affichait le message PUIS fermait la modale, en
              // effaçant la saisie qu'il fallait corriger.
              const r = await post<{ id: number; scored: number }>('/api/offers/manual', form)
              toast(t('veille.addedToast', { n: r.scored }))
              onDone()
              onClose()
              navigate(`#/offre/${r.id}`)
            }}
          >
            {t('veille.addAndEvaluate')}
          </ActionButton>
        </>
      }
    >
      <div className="field">
        <label className="field-label">{t('veille.jobTitleLabel')}</label>
        <input className="input-field" type="text" value={form.title} onChange={set('title')} placeholder={t('veille.jobTitlePlaceholder')} />
      </div>
      <div className="field">
        <label className="field-label">{t('veille.companyLabel')}</label>
        <input className="input-field" type="text" value={form.company} onChange={set('company')} placeholder={t('veille.optional')} />
      </div>
      <div className="field">
        <label className="field-label">{t('veille.locationLabel')}</label>
        <input className="input-field" type="text" value={form.location} onChange={set('location')} placeholder={t('veille.optional')} />
      </div>
      <div className="field">
        <label className="field-label">{t('veille.briefLabel')}</label>
        <textarea className="input-field" value={form.description} onChange={set('description')} placeholder={t('veille.briefPlaceholder')} />
        <div className="field-help">{t('veille.briefHelp')}</div>
      </div>
    </Modal>
  )
}
